import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchBenchmarkRuns,
  triggerBenchmarkRun,
  deleteBenchmarkRun,
  abortBenchmarkRun,
  fetchBenchmarkConfigs,
  RuleAction,
} from "@/lib/api/resources/benchmark";
import { Agent, AiProfile } from "@/lib/api/resources/agents";
import { Button } from "@/components/common/button";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Tag, Popconfirm, Tooltip, message, Checkbox } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import { MAX_COMPARE_RUNS } from "../compare/lib/align";

import { useState } from "react";
import { Table, Input } from "antd";
import type { TablePaginationConfig } from "antd/es/table";
import { effectiveInferenceParams } from "@/components/ai-connection/inferenceOptionsOverride";

export const BenchmarkRunsTable = ({
  agents = [],
  ruleActions = [],
  aiProfiles = [],
}: {
  agents?: Agent[];
  ruleActions?: RuleAction[];
  aiProfiles?: AiProfile[];
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Runs picked for side-by-side comparison (capped to keep the comparison readable).
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const toggleCompare = (id: string) =>
    setCompareIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length >= MAX_COMPARE_RUNS
          ? prev
          : [...prev, id],
    );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["benchmark_runs", currentPage, sortField, sortOrder, searchTerm],
    queryFn: () =>
      fetchBenchmarkRuns(currentPage, sortField, sortOrder, searchTerm),
    refetchInterval: (query) => {
      const runs: any[] =
        (query.state.data as any)?.member ||
        (query.state.data as any)?.["hydra:member"] ||
        [];
      return runs.some((r) => r.status === "pending" || r.status === "running")
        ? 5000
        : false;
    },
  });

  const { data: configsData } = useQuery({
    queryKey: ["benchmarkConfigs"],
    queryFn: () => fetchBenchmarkConfigs(),
  });
  const configs = Array.isArray(configsData)
    ? configsData
    : (configsData as any)?.member ||
      (configsData as any)?.["hydra:member"] ||
      [];

  const rerunMutation = useMutation({
    mutationFn: (params: {
      agentId: string;
      ruleActionId?: string;
      config?: Record<string, unknown>;
      benchmarkConfig?: string;
    }) =>
      triggerBenchmarkRun(
        String(params.agentId).split("/").pop() ?? "",
        params.ruleActionId,
        params.config,
        params.benchmarkConfig
          ? (String(params.benchmarkConfig).split("/").pop() ?? undefined)
          : undefined,
      ),
    onSuccess: () => {
      message.success("Benchmark rerun triggered successfully!");
      queryClient.invalidateQueries({ queryKey: ["benchmark_runs"] });
    },
    onError: () => {
      message.error("Failed to trigger benchmark rerun.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (runId: string) => deleteBenchmarkRun(runId),
    onSuccess: () => {
      message.success("Benchmark run deleted.");
      queryClient.invalidateQueries({ queryKey: ["benchmark_runs"] });
    },
    onError: () => {
      message.error("Failed to delete benchmark run.");
    },
  });

  const abortMutation = useMutation({
    mutationFn: (runId: string) => abortBenchmarkRun(runId),
    onSuccess: () => {
      message.success("Benchmark run aborted.");
      queryClient.invalidateQueries({ queryKey: ["benchmark_runs"] });
    },
    onError: () => {
      message.error("Failed to abort benchmark run.");
    },
  });

  const rawData = Array.isArray(data)
    ? data
    : (data as any)?.member || (data as any)?.["hydra:member"] || [];

  const totalItems = (data as any)?.["hydra:totalItems"] || rawData.length;

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: any,
    sorter: any,
  ) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
    if (sorter && sorter.columnKey) {
      setSortField(sorter.columnKey);
      setSortOrder(sorter.order === "ascend" ? "asc" : "desc");
    }
  };

  const columns = [
    {
      title: "",
      key: "select",
      width: 50,
      render: (_: any, item: any) => {
        const isPicked = compareIds.includes(item.id);
        return (
          <Checkbox
            checked={isPicked}
            disabled={!isPicked && compareIds.length >= MAX_COMPARE_RUNS}
            onChange={() => toggleCompare(item.id)}
            aria-label="Select run to compare"
          />
        );
      },
    },
    {
      title: "Agent",
      key: "agent",
      width: 200,
      render: (_: any, item: any) => {
        const agentId =
          typeof item.agent === "string"
            ? item.agent
            : item.agent?.["@id"] || item.agent?.id;
        const matchedAgent = agents.find(
          (a: any) =>
            (a["@id"] || a.id)?.toLowerCase() === agentId?.toLowerCase() ||
            agentId?.toLowerCase()?.includes(a.id?.toLowerCase()),
        );
        const name = matchedAgent
          ? matchedAgent.name
          : typeof item.agent === "object" && (item.agent as any)?.name
            ? (item.agent as any).name
            : agentId || "Unknown";

        const p = item.config?.parameters ?? {};
        const j = item.config?.judge ?? {};
        const gen = effectiveInferenceParams(p);

        const agentConnection =
          item.config?.agentAiConnection || matchedAgent?.aiConnection;
        let modelLabel =
          gen.model ||
          agentConnection?.model ||
          matchedAgent?.aiConnection?.model;
        let providerLabel =
          p.override?.provider ||
          agentConnection?.providerLabel ||
          agentConnection?.provider;

        if (!item.config?.agentAiConnection) {
          if (p?.override?.model || p?.override?.provider) {
            modelLabel = p.override.model || modelLabel;
            providerLabel = p.override.provider || providerLabel;
          } else if (p?.aiProfileId) {
            const profileId =
              typeof p.aiProfileId === "string"
                ? p.aiProfileId.split("/").pop()
                : p.aiProfileId;
            const profile = aiProfiles.find(
              (prof: any) =>
                prof.id === profileId || prof["@id"]?.endsWith(profileId),
            );
            if (profile?.aiConnection) {
              // The API key is never returned on read; use the read-safe
              // aiConnection decomposition for the model/provider labels.
              modelLabel = profile.aiConnection.model || modelLabel;
              providerLabel =
                profile.aiConnection.providerLabel ||
                profile.aiConnection.provider ||
                providerLabel;
            }
          }
        }

        if (providerLabel && providerLabel === providerLabel.toLowerCase()) {
          providerLabel =
            providerLabel.charAt(0).toUpperCase() + providerLabel.slice(1);
        }

        let configName =
          typeof item.benchmarkConfig === "object"
            ? item.benchmarkConfig?.name
            : undefined;
        if (!configName && typeof item.benchmarkConfig === "string") {
          const configId = item.benchmarkConfig.split("/").pop();
          const matchedConfig = configs.find(
            (c: any) => c.id === configId || c["@id"]?.endsWith(configId),
          );
          configName = matchedConfig?.name;
        }

        const getAgentTooltip = () => (
          <div className="text-sm py-1 max-w-xs">
            <strong className="block mb-1 text-white">
              {configName || name}
            </strong>
            {configName && name && (
              <div className="text-gray-300 mb-2 border-b border-gray-600 pb-2">
                Agent: {name}
              </div>
            )}

            <div className="mt-2 space-y-1 text-gray-200">
              {providerLabel && (
                <div>
                  <span className="text-gray-400">Provider:</span>{" "}
                  {providerLabel}
                </div>
              )}
              {modelLabel && (
                <div>
                  <span className="text-gray-400">Model:</span> {modelLabel}
                </div>
              )}
              {gen.temperature != null && (
                <div>
                  <span className="text-gray-400">Temperature:</span>{" "}
                  {gen.temperature}
                </div>
              )}
              {gen.top_p != null && (
                <div>
                  <span className="text-gray-400">Top P:</span> {gen.top_p}
                </div>
              )}
              {j.enabled && (
                <div>
                  <span className="text-gray-400">Judge:</span> Enabled{" "}
                  {j.mode ? `(${j.mode})` : ""}
                </div>
              )}
            </div>
          </div>
        );

        return (
          <Tooltip
            title={getAgentTooltip()}
            color="#1f2937"
            overlayInnerStyle={{ borderRadius: "8px" }}
            placement="topLeft"
          >
            <div className="flex flex-col gap-1.5 min-w-0 py-0.5 cursor-help">
              <span className="font-medium text-gray-900 line-clamp-2">
                {configName || name}
              </span>
              {configName && name && (
                <span className="text-xs text-gray-500 line-clamp-1">
                  Agent: {name}
                </span>
              )}
              <div className="flex flex-wrap items-center gap-1">
                {providerLabel && (
                  <Tag
                    variant="filled"
                    className="m-0 text-[10px] bg-slate-100 text-slate-600 leading-tight px-1.5 py-0.5"
                  >
                    {providerLabel}
                  </Tag>
                )}
                {modelLabel && (
                  <Tag
                    variant="filled"
                    className="m-0 text-[10px] bg-indigo-50 text-indigo-600 font-medium leading-tight px-1.5 py-0.5"
                  >
                    {modelLabel}
                  </Tag>
                )}
                {gen.temperature != null && (
                  <Tag
                    variant="filled"
                    className="m-0 text-[10px] bg-gray-50 text-gray-500 leading-tight px-1.5 py-0.5"
                  >
                    t={gen.temperature}
                  </Tag>
                )}
                {gen.top_p != null && (
                  <Tag
                    variant="filled"
                    className="m-0 text-[10px] bg-gray-50 text-gray-500 leading-tight px-1.5 py-0.5"
                  >
                    p={gen.top_p}
                  </Tag>
                )}
                {j.enabled && (
                  <Tag
                    variant="filled"
                    className="m-0 text-[10px] bg-amber-50 text-amber-600 leading-tight px-1.5 py-0.5"
                  >
                    Judge
                  </Tag>
                )}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Rule Action",
      key: "ruleAction",
      width: 200,
      render: (_: any, item: any) => {
        const rawRa = item.ruleActionId || item.ruleAction;
        const raId =
          typeof rawRa === "string" ? rawRa : rawRa?.["@id"] || rawRa?.id;
        const matchedRa = ruleActions.find(
          (ra: any) =>
            (ra["@id"] || ra.id)?.toLowerCase() === raId?.toLowerCase() ||
            raId?.toLowerCase()?.includes(ra.id?.toLowerCase()),
        );
        return raId ? (
          <span
            className="font-medium text-gray-700 line-clamp-2"
            title={matchedRa ? matchedRa.label || matchedRa.name : raId}
          >
            {matchedRa ? matchedRa.label || matchedRa.name : raId}
          </span>
        ) : (
          <span className="text-gray-400 italic text-xs">None</span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      width: 140,
      sorter: true,
      render: (status: string, item: any) => {
        let statusColor = "default";
        if (status === "running") statusColor = "blue";
        if (status === "completed") statusColor = "green";
        if (status === "failed") statusColor = "red";
        const statusLabel =
          typeof status === "string" ? status.toUpperCase() : status;

        let scoreElem = null;
        if (item.accuracyRate != null) {
          scoreElem = (
            <Tooltip
              title="Accuracy Rate: The percentage of evaluated cases that perfectly matched the expected output."
              placement="bottomLeft"
            >
              <span className="text-emerald-600 block text-xs font-semibold mt-1 cursor-help w-fit border-b border-dashed border-emerald-200 pb-0.5">
                Acc: {Math.round(item.accuracyRate * 100)}%
              </span>
            </Tooltip>
          );
        } else if (item.averageQualityScore != null) {
          scoreElem = (
            <Tooltip
              title="Average Quality Score: The average rating (out of 10) awarded by the AI Judge across all evaluated cases."
              placement="bottomLeft"
            >
              <span className="text-emerald-600 block text-xs font-semibold mt-1 cursor-help w-fit border-b border-dashed border-emerald-200 pb-0.5">
                Score: {item.averageQualityScore.toFixed(1)}
              </span>
            </Tooltip>
          );
        }

        const getTooltipContent = () => (
          <div className="text-sm py-1 max-w-xs">
            {item.status === "failed" ? (
              <>
                <strong className="block text-red-300 mb-1">Run Failed</strong>
                <div className="text-gray-100">
                  {item.statusMessage ||
                    "An unknown error occurred during execution."}
                </div>
                {item.failedCases > 0 && (
                  <div className="mt-1 text-gray-300">
                    Failed to evaluate {item.failedCases} cases.
                  </div>
                )}
              </>
            ) : item.status === "running" ? (
              <>
                <strong className="block text-blue-300 mb-1">
                  Run in Progress
                </strong>
                <div className="text-gray-100">Actively evaluating inputs.</div>
                <div className="mt-1 text-gray-300">
                  Progress: {item.completedCases || 0} of {item.totalCases || 0}{" "}
                  cases.
                </div>
              </>
            ) : item.status === "pending" ? (
              <>
                <strong className="block text-gray-300 mb-1">
                  Pending Execution
                </strong>
                <div className="text-gray-100">
                  Waiting for an available background worker to pick up this
                  benchmark run.
                </div>
              </>
            ) : item.status === "completed" ? (
              <>
                <strong className="block text-green-300 mb-1">
                  Run Completed
                </strong>
                <div className="text-gray-100">
                  Successfully finished evaluating {item.completedCases || 0}{" "}
                  cases.
                </div>
                {item.failedCases > 0 && (
                  <div className="mt-1 text-red-300">
                    Note: {item.failedCases} individual case(s) failed during
                    evaluation.
                  </div>
                )}
              </>
            ) : (
              <div className="text-gray-100">
                {item.statusMessage || `Status: ${item.status}`}
              </div>
            )}
          </div>
        );

        return (
          <div className="flex flex-col min-w-0 py-0.5 items-start">
            <Tooltip
              title={getTooltipContent()}
              color="#1f2937"
              overlayInnerStyle={{ borderRadius: "8px" }}
            >
              <Tag color={statusColor} className="cursor-help m-0">
                {statusLabel} {item.status === "failed" && "ⓘ"}
              </Tag>
            </Tooltip>
            {scoreElem}
          </div>
        );
      },
    },

    {
      title: "Progress",
      key: "progress",
      width: 100,
      render: (_: any, item: any) => {
        return (
          <div className="flex flex-col min-w-0 py-0.5">
            <span className="text-sm whitespace-nowrap">
              {item.completedCases || 0} / {item.totalCases || 0}
            </span>
            {item.failedCases > 0 && (
              <span className="text-red-500 block text-xs">
                ({item.failedCases} err)
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Created",
      key: "createdAt",
      dataIndex: "createdAt",
      width: 90,
      sorter: true,
      render: (createdAt: string) => (
        <div className="flex flex-col text-gray-500 text-sm">
          <span>{dayjs(createdAt).format("DD-MM-YYYY")}</span>
          <span className="text-xs text-gray-400">
            {dayjs(createdAt).format("HH:mm")}
          </span>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right" as const,
      fixed: "right" as const,
      width: 220,
      className: "bg-white z-10",
      render: (_: any, item: any) => {
        // Calculate actual raw agent/ruleaction ID for rerun
        const agentId =
          typeof item.agent === "string"
            ? item.agent
            : item.agent?.["@id"] || item.agent?.id;
        const matchedAgent = agents.find(
          (a: any) =>
            (a["@id"] || a.id)?.toLowerCase() === agentId?.toLowerCase() ||
            agentId?.toLowerCase()?.includes(a.id?.toLowerCase()),
        );
        const cleanAgentId =
          matchedAgent?.id ||
          (typeof item.agent === "string"
            ? item.agent.split("/").pop()
            : item.agent?.id);

        const rawRa = item.ruleActionId || item.ruleAction;
        const raId =
          typeof rawRa === "string" ? rawRa : rawRa?.["@id"] || rawRa?.id;
        const matchedRa = ruleActions.find(
          (ra: any) =>
            (ra["@id"] || ra.id)?.toLowerCase() === raId?.toLowerCase() ||
            raId?.toLowerCase()?.includes(ra.id?.toLowerCase()),
        );
        const cleanRaId =
          matchedRa?.id ||
          (typeof item.ruleActionId === "string"
            ? item.ruleActionId.split("/").pop()
            : undefined);

        return (
          <div className="flex items-center justify-end gap-1 bg-white">
            <Link to={`/benchmark/${item.id}`}>
              <Button
                variant="outline"
                size="sm"
                className="px-2 py-1 h-auto text-xs"
              >
                Details
              </Button>
            </Link>
            <Popconfirm
              title="Rerun this benchmark?"
              description="Are you sure you want to spawn a new run using these exact same parameters?"
              onConfirm={() => {
                const bConfId =
                  typeof item.benchmarkConfig === "string"
                    ? item.benchmarkConfig.split("/").pop()
                    : item.benchmarkConfig?.id;

                const cleanConfig = item.config || {};

                if (cleanAgentId) {
                  rerunMutation.mutate({
                    agentId: cleanAgentId,
                    ruleActionId: cleanRaId,
                    config: cleanConfig,
                    benchmarkConfig: bConfId,
                  });
                }
              }}
              okText="Yes, Rerun"
              cancelText="Cancel"
              placement="topLeft"
              disabled={rerunMutation.isPending || !cleanAgentId}
            >
              <Button
                variant="outline"
                size="sm"
                className="px-2 py-1 h-auto text-xs"
                disabled={rerunMutation.isPending || !cleanAgentId}
              >
                Rerun
              </Button>
            </Popconfirm>
            {(item.status === "running" || item.status === "pending") && (
              <Popconfirm
                title="Abort this run?"
                description="Are you sure you want to abort this benchmark run?"
                onConfirm={() => abortMutation.mutate(item.id)}
                okText="Yes, Abort"
                cancelText="No"
                placement="topLeft"
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="px-2 py-1 h-auto text-xs text-orange-500 hover:bg-orange-50 hover:text-orange-600 border-orange-200"
                >
                  Abort
                </Button>
              </Popconfirm>
            )}
            <Popconfirm
              title="Delete this run?"
              description="Are you sure you want to delete this benchmark run?"
              onConfirm={() => deleteMutation.mutate(item.id)}
              okText="Yes"
              cancelText="No"
              placement="topLeft"
            >
              <Button
                variant="outline"
                size="sm"
                className="px-2 py-1 h-auto text-xs text-red-500 hover:bg-red-50 hover:text-red-600 border-red-200"
              >
                Delete
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap px-4 pt-4">
        <span className="text-xs text-gray-500">
          <SwapOutlined className="mr-1" />
          Tick two or more runs to compare them side by side.
        </span>
        {compareIds.length > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5">
            <span className="text-sm text-indigo-900">
              <strong>{compareIds.length}</strong> selected
              {compareIds.length < 2 && (
                <span className="text-indigo-500"> · pick at least 2</span>
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCompareIds([])}
            >
              Clear
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={compareIds.length < 2}
              onClick={() =>
                navigate(`/benchmark/compare?runs=${compareIds.join(",")}`)
              }
            >
              <SwapOutlined className="mr-1" />
              Compare{compareIds.length >= 2 ? ` (${compareIds.length})` : ""}
            </Button>
          </div>
        )}
      </div>
      <div className="flex justify-between items-center w-full px-4">
        <Input.Search
          placeholder="Search runs..."
          allowClear
          onSearch={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          style={{ width: 300 }}
        />
      </div>
      <Table
        columns={columns}
        dataSource={rawData}
        rowKey="id"
        loading={isLoading}
        scroll={{ x: "max-content" }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
      />
    </div>
  );
};
